import { useState } from 'react';

interface NpsHistoryListProps {
  monthly: Array<{ month: string; score: number; total: number }>;
}

const PAGE_SIZE = 10;

function npsColor(score: number): string {
  if (score < 0) return 'var(--bp-error, #ef4444)';
  if (score < 50) return 'var(--bp-warning, #f59e0b)';
  return 'var(--bp-success, #22c55e)';
}

function formatMonth(ym: string): string {
  const [year, month] = ym.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

export function NpsHistoryList({ monthly }: NpsHistoryListProps) {
  const [page, setPage] = useState(0);
  const start = page * PAGE_SIZE;
  const items = monthly.slice(start, start + PAGE_SIZE);
  const totalPages = Math.ceil(monthly.length / PAGE_SIZE);

  if (monthly.length === 0) {
    return (
      <div style={{
        padding: '16px', borderRadius: 12,
        background: 'var(--bp-surface-raised, #1a2332)',
        border: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Rating History</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--bp-text-muted, #64748b)' }}>
          No ratings recorded yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px 16px', borderRadius: 12,
      background: 'var(--bp-surface-raised, #1a2332)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '0.9rem', fontWeight: 600 }}>Rating History</h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item) => (
          <div
            key={item.month}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <span style={{ fontSize: '0.8rem', color: 'var(--bp-text-secondary, #94a3b8)' }}>
              {formatMonth(item.month)}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--bp-text-muted, #64748b)' }}>
                {item.total} rating{item.total !== 1 ? 's' : ''}
              </span>
              <span style={{
                fontSize: '0.85rem', fontWeight: 700, color: npsColor(item.score),
                minWidth: 32, textAlign: 'right',
              }}>
                {item.score}
              </span>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button
            type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            style={{
              padding: '4px 12px', borderRadius: 6, fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.05)', border: 'none',
              color: page === 0 ? 'var(--bp-text-muted, #64748b)' : 'var(--bp-text, #f1f5f9)',
              cursor: page === 0 ? 'default' : 'pointer',
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted, #64748b)', alignSelf: 'center' }}>
            {page + 1} / {totalPages}
          </span>
          <button
            type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{
              padding: '4px 12px', borderRadius: 6, fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.05)', border: 'none',
              color: page >= totalPages - 1 ? 'var(--bp-text-muted, #64748b)' : 'var(--bp-text, #f1f5f9)',
              cursor: page >= totalPages - 1 ? 'default' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
